import { FC, useMemo, useState } from 'react';
import { SearchFilter } from '@components/Filters/SearchFilter';
import {
  Stack,
  Table,
  TableColumn,
  TableNoRecords,
} from '@servicetitan/design-system';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import ErrorComponent from '@components/ErrorComponent';
import { OutboundCallDetails } from '../../types';
import { OutboundCallEntity } from '@lib/types/common-call-types';
import RadioCell from './RadioCell';
import EstimatedWaitCell from './EstimatedWaitCell';
import { QueueRow } from './types';

const PAGE_SIZE = 4;

/**
 * Shared queue table used in both the outbound call modal and transfer modal.
 * Owns all UI state: search, sort, pagination, and row selection.
 *
 * `initialSort` controls the default sort order, which differs per context
 * (e.g. `estimatedWait asc` for outbound, `name asc` for transfer).
 */
interface QueuesTableProps {
  queues: QueueRow[];
  loading: boolean;
  setCallDetails: (details: OutboundCallDetails) => void;
  initialSort?: SortDescriptor[];
}

const QueuesTable: FC<QueuesTableProps> = ({
  queues,
  loading,
  setCallDetails,
  initialSort = [],
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedQueue, setSelectedQueue] = useState('');
  const [sort, setSort] = useState<SortDescriptor[]>(initialSort);
  const [page, setPage] = useState({ skip: 0, take: PAGE_SIZE });

  const filteredQueues = useMemo(() => {
    const filtered = !searchTerm
      ? queues
      : queues.filter((q) =>
          q.name.toLowerCase().includes(searchTerm.toLowerCase()),
        );
    return orderBy(filtered, sort);
  }, [searchTerm, queues, sort]);

  const rows = useMemo(
    () => filteredQueues.slice(page.skip, page.skip + page.take),
    [filteredQueues, page],
  );

  const handlePageChange = (e: { page: { take: number; skip: number } }) => {
    if (page.take !== e.page.take) {
      setPage({ skip: 0, take: e.page.take });
    } else {
      setPage({ skip: e.page.skip, take: e.page.take });
    }
  };

  return (
    <Stack direction='column' spacing={2}>
      <Stack.Item>
        <SearchFilter
          placeholder='Search for a queue'
          className='w-100'
          value={searchTerm}
          onChange={setSearchTerm}
        />
      </Stack.Item>
      <Stack.Item>
        <Table
          borders={{ vertical: false, horizontal: true }}
          loading={loading}
          data={rows}
          sortable={true}
          sort={sort}
          onSortChange={(e) => setSort(e.sort)}
          pageable={{ buttonCount: 3 }}
          total={filteredQueues.length}
          skip={page.skip}
          pageSize={page.take}
          striped={false}
          onPageChange={handlePageChange}
        >
          <TableNoRecords>
            <div className='ta-center m-x-auto p-y-6' style={{ width: 420 }}>
              <ErrorComponent
                image='block'
                title='No results found'
                body='Try another search.'
              />
            </div>
          </TableNoRecords>
          <TableColumn
            width={40}
            cell={(props) =>
              RadioCell({
                dataItem: props.dataItem,
                selectedQueue: selectedQueue,
                onSelect: (queue) => {
                  setSelectedQueue(queue.id);
                  setCallDetails({
                    entity: OutboundCallEntity.Queue,
                    number: queue.number,
                    callDisplayName: queue.name,
                    queueId: queue.id,
                  });
                },
              })
            }
          />
          <TableColumn field='name' title='Queue' />
          <TableColumn field='availableUsers' title='Employees' />
          <TableColumn
            field='estimatedWait'
            title='Est. Wait Time'
            cell={(props) =>
              EstimatedWaitCell({
                dataItem: props.dataItem,
              })
            }
          />
        </Table>
      </Stack.Item>
    </Stack>
  );
};

export default QueuesTable;
